import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, unref as _unref, normalizeClass as _normalizeClass } from "vue"
import _imports_0 from '@/assets/gold-logo-transparent.png'


const _hoisted_1 = { class: "min-h-screen bg-black-primary text-white font-inter p-6" }
const _hoisted_2 = { class: "max-w-7xl mx-auto" }
const _hoisted_3 = { class: "mb-6 flex items-center gap-3" }
const _hoisted_4 = {
  key: 0,
  class: "flex items-center justify-center py-16"
}
const _hoisted_5 = {
  key: 1,
  class: "space-y-8"
}
const _hoisted_6 = { class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" }
const _hoisted_7 = { class: "flex items-center justify-between mb-4" }
const _hoisted_8 = { class: "text-xl font-semibold text-gold" }
const _hoisted_9 = { class: "text-sm text-gray-400" }
const _hoisted_10 = { class: "text-right" }
const _hoisted_11 = { class: "text-2xl font-bold" }
const _hoisted_12 = { class: "grid grid-cols-2 gap-3 text-sm" }
const _hoisted_13 = { class: "bg-black-primary/60 rounded-lg p-3" }
const _hoisted_14 = { class: "text-2xl font-bold" }
const _hoisted_15 = { class: "bg-black-primary/60 rounded-lg p-3" }
const _hoisted_16 = { class: "text-2xl font-bold" }
const _hoisted_17 = { class: "bg-black-primary/60 rounded-lg p-3" }
const _hoisted_18 = { class: "text-2xl font-bold text-green-400" }
const _hoisted_19 = { class: "bg-black-primary/60 rounded-lg p-3" }
const _hoisted_20 = { class: "text-2xl font-bold text-yellow-400" }
const _hoisted_21 = { class: "mt-4 text-sm" }
const _hoisted_22 = { class: "font-mono tracking-widest text-white" }
const _hoisted_23 = { class: "mt-4" }
const _hoisted_24 = { class: "mt-3" }
const _hoisted_25 = { class: "max-h-40 overflow-y-auto pr-1" }
const _hoisted_26 = { class: "w-full text-left text-sm" }
const _hoisted_27 = { class: "py-1" }
const _hoisted_28 = { class: "py-1" }
const _hoisted_29 = { class: "py-1" }
const _hoisted_30 = { class: "max-h-40 overflow-y-auto pr-1" }
const _hoisted_31 = { class: "w-full text-left text-sm" }
const _hoisted_32 = { class: "py-1" }
const _hoisted_33 = { class: "py-1" }
const _hoisted_34 = { class: "py-1" }
const _hoisted_35 = { class: "py-1" }

import { onMounted, ref, computed } from 'vue';
import UserService from '@/service/user.service';
import RequestService from '@/service/request.service';
import LoadingSpinner from '@/components/admin-dashboard/LoadingSpinner.vue';
import { formatFirebaseTimestamp } from '@/helpers';

type PartnerRow = {
  userId: string;
  fullName: string;
  email: string;
  referralCode?: string | null;
  totalLeads: number;
  totalRequests: number;
  paidRequests: number;
  unpaidRequests: number;
  referralAmount: number; // derived
  leads: any[];
  requests: any[];
};

const referralCommissionRate = 0.1; // 10% of request amount if available


export default /*@__PURE__*/_defineComponent({
  __name: 'PartnersOverview',
  setup(__props) {

const loading = ref(true);
const partners = ref<PartnerRow[]>([]);
const search = ref('');
function safeDate(date: any) {
  if (!date) return '—';
  if (date.toDate) return date.toDate();
  if (date.seconds) return new Date(date.seconds * 1000);
  return new Date(date);
}

async function loadData() {
  loading.value = true;
  const [partnerUsers, referralsMap] = await Promise.all([
    UserService.getPartners(),
    UserService.getAllReferralsMap(),
  ]);

  const allPartnerIds = partnerUsers.map((p: any) => p.userId);

  const partnerLeadsResults = await Promise.all(
    allPartnerIds.map((pid: string) => UserService.getUsersReferredBy(pid)),
  );

  const partnerRequestsResults = await Promise.all(
    partnerLeadsResults.map((leads: any[]) => RequestService.getRequestsByUserIdsWithDerived(leads.map((l: any) => l.userId))),
  );

  partners.value = partnerUsers.map((p: any, index: number) => {
    const leads = partnerLeadsResults[index] || [];
    const requests = partnerRequestsResults[index] || [];
    const paid = requests.filter((r: any) => r.isPaid);
    const unpaid = requests.length - paid.length;

    // Derive referral amount: sum of (amount * rate) if available, else per-paid flat amount
    const amountSum = requests.reduce((acc: number, r: any) => {
      const isPaid = r.isPaid;
      // eslint-disable-next-line no-nested-ternary
      const amount = typeof r.total === 'number' ? r.total : (typeof r.amount === 'number' ? r.amount : 0);
      if (isPaid && amount > 0) return acc + amount * referralCommissionRate;
      return acc;
    }, 0);

    const referralAmount = Math.round((amountSum + (paid.length && amountSum === 0 ? paid.length * 5 : 0)) * 100) / 100; // fallback £5 per paid if no amounts

    return {
      userId: p.userId,
      fullName: p.fullName || p.name || '—',
      email: p.email || '—',
      referralCode: referralsMap[p.userId] || null,
      totalLeads: leads.length,
      totalRequests: requests.length,
      paidRequests: paid.length,
      unpaidRequests: unpaid,
      referralAmount,
      leads,
      requests,
    } as PartnerRow;
  });

  loading.value = false;
}

const filteredPartners = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return partners.value;
  return partners.value.filter((p) => p.fullName.toLowerCase().includes(term)
    || p.email.toLowerCase().includes(term)
    || (p.referralCode || '').toLowerCase().includes(term));
});

onMounted(loadData);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[12] || (_cache[12] = _createElementVNode("div", { class: "flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4" }, [
        _createElementVNode("div", null, [
          _createElementVNode("h1", { class: "text-3xl md:text-4xl font-bold text-gold mb-2" }, "Partners Overview"),
          _createElementVNode("p", { class: "text-lg text-gray-300" }, "Overview of partner performance and referrals.")
        ]),
        _createElementVNode("img", {
          src: _imports_0,
          alt: "TALAB Logo",
          class: "w-24 md:w-32"
        })
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
          placeholder: "Search partners...",
          class: "px-4 py-2 rounded-lg bg-black-secondary border border-zinc-700 focus:outline-none focus:border-gold-primary w-full md:w-80"
        }, null, 512), [
          [_vModelText, search.value]
        ])
      ]),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(LoadingSpinner)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredPartners.value, (p) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: p.userId,
                  class: "bg-neutral-800 rounded-2xl p-6 shadow-lg"
                }, [
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("div", null, [
                      _createElementVNode("h3", _hoisted_8, _toDisplayString(p.fullName), 1),
                      _createElementVNode("p", _hoisted_9, _toDisplayString(p.email), 1)
                    ]),
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("div", _hoisted_11, "£" + _toDisplayString(p.referralAmount.toFixed(2)), 1),
                      _cache[1] || (_cache[1] = _createElementVNode("div", { class: "text-xs text-gray-400" }, "Referral Amount", -1))
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("div", _hoisted_14, _toDisplayString(p.totalLeads), 1),
                      _cache[2] || (_cache[2] = _createElementVNode("div", { class: "text-xs text-gray-400" }, "Leads", -1))
                    ]),
                    _createElementVNode("div", _hoisted_15, [
                      _createElementVNode("div", _hoisted_16, _toDisplayString(p.totalRequests), 1),
                      _cache[3] || (_cache[3] = _createElementVNode("div", { class: "text-xs text-gray-400" }, "Requests", -1))
                    ]),
                    _createElementVNode("div", _hoisted_17, [
                      _createElementVNode("div", _hoisted_18, _toDisplayString(p.paidRequests), 1),
                      _cache[4] || (_cache[4] = _createElementVNode("div", { class: "text-xs text-gray-400" }, "Paid", -1))
                    ]),
                    _createElementVNode("div", _hoisted_19, [
                      _createElementVNode("div", _hoisted_20, _toDisplayString(p.unpaidRequests), 1),
                      _cache[5] || (_cache[5] = _createElementVNode("div", { class: "text-xs text-gray-400" }, "Unpaid", -1))
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_21, [
                    _cache[6] || (_cache[6] = _createElementVNode("div", { class: "text-gray-400" }, "Referral Code", -1)),
                    _createElementVNode("div", _hoisted_22, _toDisplayString(p.referralCode || 'N/A'), 1)
                  ]),
                  _createElementVNode("details", _hoisted_23, [
                    _cache[11] || (_cache[11] = _createElementVNode("summary", { class: "cursor-pointer text-gold" }, "View activity", -1)),
                    _createElementVNode("div", _hoisted_24, [
                      _cache[9] || (_cache[9] = _createElementVNode("div", { class: "text-sm text-gray-400 mb-2" }, "Recent Leads", -1)),
                      _createElementVNode("div", _hoisted_25, [
                        _createElementVNode("table", _hoisted_26, [
                          _cache[7] || (_cache[7] = _createElementVNode("thead", null, [
                            _createElementVNode("tr", { class: "text-gold/90" }, [
                              _createElementVNode("th", { class: "py-1" }, "Name"),
                              _createElementVNode("th", { class: "py-1" }, "Email"),
                              _createElementVNode("th", { class: "py-1" }, "Registered")
                            ])
                          ], -1)),
                          _createElementVNode("tbody", null, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(p.leads, (l) => {
                              return (_openBlock(), _createElementBlock("tr", {
                                key: l.userId,
                                class: "border-b border-zinc-700/50"
                              }, [
                                _createElementVNode("td", _hoisted_27, _toDisplayString(l.fullName), 1),
                                _createElementVNode("td", _hoisted_28, _toDisplayString(l.email), 1),
                                _createElementVNode("td", _hoisted_29, _toDisplayString(_unref(formatFirebaseTimestamp)(l.registrationDate, true)), 1)
                              ]))
                            }), 128))
                          ])
                        ])
                      ]),
                      _cache[10] || (_cache[10] = _createElementVNode("div", { class: "text-sm text-gray-400 mt-4 mb-2" }, "Recent Requests", -1)),
                      _createElementVNode("div", _hoisted_30, [
                        _createElementVNode("table", _hoisted_31, [
                          _cache[8] || (_cache[8] = _createElementVNode("thead", null, [
                            _createElementVNode("tr", { class: "text-gold/90" }, [
                              _createElementVNode("th", { class: "py-1" }, "Lead"),
                              _createElementVNode("th", { class: "py-1" }, "Service"),
                              _createElementVNode("th", { class: "py-1" }, "Date"),
                              _createElementVNode("th", { class: "py-1" }, "Status")
                            ])
                          ], -1)),
                          _createElementVNode("tbody", null, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(p.requests, (r) => {
                              return (_openBlock(), _createElementBlock("tr", {
                                key: r.id,
                                class: "border-b border-zinc-700/50"
                              }, [
                                _createElementVNode("td", _hoisted_32, _toDisplayString(r.userId), 1),
                                _createElementVNode("td", _hoisted_33, _toDisplayString(r.title || r.service || '—'), 1),
                                _createElementVNode("td", _hoisted_34, _toDisplayString(safeDate(r.createdAt).toLocaleDateString()), 1),
                                _createElementVNode("td", _hoisted_35, [
                                  _createElementVNode("span", {
                                    class: _normalizeClass(r.isPaid ? 'text-green-400' : 'text-yellow-400')
                                  }, _toDisplayString(r.isPaid ? 'paid' : (r.status || 'pending')), 3)
                                ])
                              ]))
                            }), 128))
                          ])
                        ])
                      ])
                    ])
                  ])
                ]))
              }), 128))
            ])
          ]))
    ])
  ]))
}
}

})